import ms from "ms"
import { resolveMcpConnectionCommandDigestKeys } from '@aient/types/mcp-connections/command-digest-keys'

export type AientEnv = 'test' | 'dev' | 'preview' | 'prod'

// Execution environment
// NOTE: These must be defined before `AIENT_ENV` is computed.
// `AIENT_ENV` is computed during module evaluation; if `parseAientEnv()` reads
// exports declared later in this file, we can hit the temporal dead zone.
export const NODE_ENV = process.env.NODE_ENV ?? 'development'
export const ENABLE_SECURITY_HEADERS = process.env.ENABLE_SECURITY_HEADERS === 'true'
export const AGENT_LIST_SHORT_CURSORS_ENABLED = process.env.AIENT_AGENT_LIST_SHORT_CURSORS_ENABLED === 'true'
export const NEXT_PUBLIC_TEST_MODE = process.env.NEXT_PUBLIC_TEST_MODE === 'true'
export const ORGANISATION_LIFECYCLE_INGRESS_V2_ENABLED =
  process.env.ORGANISATION_LIFECYCLE_INGRESS_V2_ENABLED === 'true'
export const DEBUG_ARTIFACT_BACKFILL_WRITER_ENABLED =
  process.env.DEBUG_ARTIFACT_BACKFILL_WRITER_ENABLED === 'true'

/** The connector hub's public origin and the connector image an attach manifest pins. */
export function kubernetesConnectorSettings(): { brokerUrl: string, image: string } {
  return {
    brokerUrl: process.env.KUBERNETES_CONNECTOR_BROKER_URL ?? 'wss://kube-broker.aient.ai',
    image: process.env.KUBERNETES_CONNECTOR_IMAGE ?? 'ghcr.io/aient-ai/kubernetes-connector:0.1.0@sha256:0b95e2e8c68a63f348be11a870dadb222198057c21cbb5494884494f3bf8f6a3',
  }
}

/** Activate generic customer MCP OAuth setup only after Pulse and executor serve it. */
export function isMcpOAuthProviderSetupEnabled(): boolean {
  return process.env.MCP_OAUTH_PROVIDER_SETUP_ENABLED === 'true'
}

/**
 * Temporary rollout gate for source-map commit admission. It is deliberately
 * evaluated per request so focused tests and long-lived local processes can
 * change the setting without reloading this module. Production activation
 * still happens through an explicit deployment configuration change.
 */
export function isSourcemapUploadMaintenanceEnabled(): boolean {
  return process.env.SOURCEMAP_UPLOAD_MAINTENANCE_ENABLED === 'true'
}

function normalizeAientEnv(value: string | undefined): AientEnv | undefined {
  if (value == null) return undefined
  const normalized = value.trim()
  if (normalized === '') return undefined

  // Canonical values
  if (normalized === 'test' || normalized === 'dev' || normalized === 'preview' || normalized === 'prod') return normalized

  // Common platform aliases
  if (normalized === 'production') return 'prod'
  if (normalized === 'development') return 'dev'

  return undefined
}

function parseAientEnv(value: string | undefined): AientEnv {
  const normalized = normalizeAientEnv(value)
  if (normalized != null) return normalized

  if (NEXT_PUBLIC_TEST_MODE) return 'test'
  return NODE_ENV === 'production' ? 'prod' : 'dev'
}

const CONFIGURED_AIENT_ENV = process.env.AIENT_ENV
  ?? process.env.NEXT_PUBLIC_AIENT_ENV
  ?? process.env.VERCEL_ENV
  ?? process.env.NEXT_PUBLIC_VERCEL_ENV
  ?? process.env.NEXT_PUBLIC_ENV

export const AIENT_ENV: AientEnv = parseAientEnv(CONFIGURED_AIENT_ENV)

const DEFAULT_GITHUB_APP_CONFIG_BY_ENV = {
  test: {
    id: '3127227',
    clientId: 'Iv23liZc26VDJRhijybJ',
    slug: 'aient-local',
  },
  dev: {
    id: '3127227',
    clientId: 'Iv23liZc26VDJRhijybJ',
    slug: 'aient-local',
  },
  preview: {
    id: '1943813',
    clientId: 'Iv23liTeVNNas5TlWT6g',
    slug: 'aient-ai',
  },
  prod: {
    id: '1943813',
    clientId: 'Iv23liTeVNNas5TlWT6g',
    slug: 'aient-ai',
  },
} as const satisfies Record<AientEnv, {
  id: string
  slug: string
  clientId: string
}>

const DEFAULT_GITHUB_APP_CONFIG = DEFAULT_GITHUB_APP_CONFIG_BY_ENV[AIENT_ENV]

const DEFAULT_NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY = 'pk_test_51S7MGQFpbju8kE9QleQtn9Q1XGsR3tm9eZn8D3f5ZNbIPq9UEYjs8pLc4usTX9TCIFOyTyaU8eRhZQ0QssVNOvbE00OoipydvI'
const DEFAULT_STRIPE_SECRET_KEY = 'sk_test_51S7MGQFpbju8kE9QPakv2PkgySrsiosstpMEVduRlYYJmCqwbPjHeDPs8gLJff3CaKRw0uW4A6hHgxyAICnfZhIf00ehQ2lavw'
const DEFAULT_STRIPE_SNAPSHOT_WEBHOOK_SECRET = 'whsec_y2QIxG58qAk7Ftd72dg2Rynd4KAUJFy1'
const DEFAULT_STRIPE_THIN_WEBHOOK_SECRET = 'whsec_bbQuNug8wZNPeFgL6FqPqbiFCysDtYnq'
const DEFAULT_NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID = 'price_1SugpkFpbju8kE9QfRssCV7P'
const DEFAULT_NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID = 'price_1SutUEFpbju8kE9QoqlJ1KdY'

export const DEBUG = process.env.DEBUG === 'true'
/**
 * Overrides the blog content directory (default: `<cwd>/content/blog`).
 * Set by the E2E stack to point at fixture posts so the MDX/Mermaid pipeline
 * is exercised in CI without publishing anything to production content.
 */
export const BLOG_CONTENT_DIR = process.env.BLOG_CONTENT_DIR ?? ''
/**
 * True in Vercel build/runtime containers (Amazon Linux). The blog's
 * build-time Mermaid renderer must use @sparticuz/chromium there because
 * Playwright's stock Chromium cannot load its shared libraries on that image.
 */
export const IS_VERCEL = process.env.VERCEL === '1'
export const APP_URL = process.env.NEXT_PUBLIC_APP_URL ?? process.env.APP_URL ?? process.env.E2E_BASE_URL ?? 'http://localhost:3009'
export const NEXT_PUBLIC_SUPABASE_REDIRECT_URL = process.env.NEXT_PUBLIC_SUPABASE_REDIRECT_URL ?? `${APP_URL}/auth/callback`

// GitHub App installation/auth is configured separately from Supabase's GitHub OAuth login app.
export const GITHUB_APP_ID = process.env.GITHUB_APP_ID ?? DEFAULT_GITHUB_APP_CONFIG.id
export const GITHUB_APP_SLUG = process.env.GITHUB_APP_SLUG ?? DEFAULT_GITHUB_APP_CONFIG.slug
export const GITHUB_APP_CLIENT_ID = process.env.GITHUB_APP_CLIENT_ID ?? DEFAULT_GITHUB_APP_CONFIG.clientId
export const GITHUB_APP_CLIENT_SECRET = process.env.GITHUB_APP_CLIENT_SECRET ?? ''
export const GITHUB_APP_PRIVATE_KEY = process.env.GITHUB_APP_PRIVATE_KEY ?? ''
export const GITHUB_APP_WEBHOOK_SECRET = process.env.GITHUB_APP_WEBHOOK_SECRET ?? ''
/** Reject GitHub callbacks with missing/invalid CSRF state. On by default.
 *  Set to 'false' to disable if GitHub drops state on App install redirects. */
export const GITHUB_STRICT_STATE_CHECK = process.env.GITHUB_STRICT_STATE_CHECK !== 'false'
export const GITHUB_APP_PRIVATE_KEY_BASE64 = process.env.GITHUB_APP_PRIVATE_KEY_BASE64 ?? ''
export const NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? DEFAULT_NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
export const STRIPE_SECRET_KEY = process.env.STRIPE_SECRET_KEY ?? DEFAULT_STRIPE_SECRET_KEY
export const STRIPE_SNAPSHOT_WEBHOOK_SECRET = process.env.STRIPE_SNAPSHOT_WEBHOOK_SECRET ?? DEFAULT_STRIPE_SNAPSHOT_WEBHOOK_SECRET
export const STRIPE_THIN_WEBHOOK_SECRET = process.env.STRIPE_THIN_WEBHOOK_SECRET ?? DEFAULT_STRIPE_THIN_WEBHOOK_SECRET
const DEFAULT_VAULT_KEY = '187d9d5eb362ee347b60ee023638b68b94e703a17878219b41e45d787b768784'
const EXPLICIT_VAULT_KEY = process.env.VAULT_KEY?.trim()
export const VAULT_KEY = EXPLICIT_VAULT_KEY || DEFAULT_VAULT_KEY
export const VAULT_KEY_SECONDARY = process.env.VAULT_KEY_SECONDARY
export const VAULT_KEY_SECONDARY_EXPIRES_AT = process.env.VAULT_KEY_SECONDARY_EXPIRES_AT
export const VAULT_KEY_ROTATION_PHASE = process.env.VAULT_KEY_ROTATION_PHASE
export const MCP_CONNECTION_COMMAND_DIGEST_KEYS = resolveMcpConnectionCommandDigestKeys({
  configuredValue: process.env.MCP_CONNECTION_COMMAND_DIGEST_KEYS,
  fallbackKey: VAULT_KEY,
  production: AIENT_ENV === 'prod',
})

// Account deletion anonymization salt (used for generating stable anon IDs)
export const ANON_SALT = process.env.ANON_SALT ?? 'default-salt-change-in-production'

// MinIO S3-compatible storage
export const MINIO_ENDPOINT = process.env.MINIO_ENDPOINT ?? 'http://localhost:9000'
export const MINIO_ACCESS_KEY = process.env.MINIO_ACCESS_KEY ?? 'minio'
export const MINIO_SECRET_KEY = process.env.MINIO_SECRET_KEY ?? '165477C3F09A452AAEBC35415C5220C8'
export const MINIO_BUCKET = process.env.MINIO_BUCKET ?? 'aient'
export const MINIO_REGION = process.env.MINIO_REGION ?? 'us-east-1'

// Resend email service
export const RESEND_API_KEY = process.env.RESEND_API_KEY ?? ''
export const RESEND_FROM_EMAIL = process.env.RESEND_FROM_EMAIL ?? 'Aient <noreply@aient.ai>'
export const RESEND_INBOUND_WEBHOOK_SECRET = process.env.RESEND_INBOUND_WEBHOOK_SECRET ?? ''

// Anthropic (server-side LLM calls — catch-me-up summaries, etc.)
export const ANTHROPIC_API_KEY = process.env.ANTHROPIC_API_KEY ?? ''

// OpenAI (server-side LLM calls — Needs Attention Now selection, etc.)
export const OPENAI_API_KEY = process.env.OPENAI_API_KEY ?? ''

// Slack OAuth (OIDC-like) client configuration
export const SLACK_CLIENT_ID = process.env.SLACK_CLIENT_ID ?? '9525020880211.9526851791029'
export const SLACK_CLIENT_SECRET = process.env.SLACK_CLIENT_SECRET

// Microsoft Teams: bot app id is public + committed in apps/aient-app/.env;
// the client secret stays out of git and lives in .env.local + deployment env.
export const MICROSOFT_TEAMS_BOT_APP_ID = process.env.MICROSOFT_TEAMS_BOT_APP_ID
export const MICROSOFT_TEAMS_TENANT_ID = process.env.MICROSOFT_TEAMS_TENANT_ID
export const MICROSOFT_TEAMS_PUBLIC_DOMAIN = process.env.MICROSOFT_TEAMS_PUBLIC_DOMAIN
export const MICROSOFT_TEAMS_CLIENT_SECRET = process.env.MICROSOFT_TEAMS_CLIENT_SECRET

export const SLACK_SCOPES = process.env.SLACK_SCOPES ?? [
  'app_mentions:read',
  'assistant:write',
  'canvases:read',
  'canvases:write',
  'channels:history',
  'channels:join',
  'channels:read',
  'chat:write',
  'chat:write.customize',
  'commands',
  'files:read',
  'files:write',
  'groups:history',
  'groups:read',
  'im:history',
  'im:read',
  'im:write',
  'metadata.message:read',
  'mpim:history',
  'reactions:write',
  'team:read',
  'users.profile:read',
  'users:read',
  'users:read.email',
].join(',')
export const SLACK_IDENTITY_SCOPES = process.env.SLACK_IDENTITY_SCOPES ?? 'openid profile email'
export const SLACK_SIGNING_SECRET = process.env.SLACK_SIGNING_SECRET

export const NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID =
  process.env.NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID || DEFAULT_NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID
export const NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID =
  process.env.NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID || DEFAULT_NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID

// Comma-separated list of allowed Stripe Price IDs for Checkout safety.
export const ALLOWED_PRICE_IDS = Array.from(new Set([
  ...(process.env.ALLOWED_PRICE_IDS ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean),
  NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID,
  NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID,
]))

// Supabase configuration
export const NEXT_PUBLIC_SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL
// Server requests can use the same Supabase over its internal network address.
export const SUPABASE_SERVER_URL = process.env.SUPABASE_URL?.trim() || NEXT_PUBLIC_SUPABASE_URL
// Browser sessions retain their public project namespace regardless of server transport.
export const SUPABASE_AUTH_COOKIE_NAME = NEXT_PUBLIC_SUPABASE_URL
  ? `sb-${new URL(NEXT_PUBLIC_SUPABASE_URL).hostname.split('.')[0]}-auth-token`
  : undefined
export const NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? 'sb_publishable_ACJWlzQHlZjBrEguHvfOxg_3BJgxAaH'
export const SUPABASE_SECRET_KEY = process.env.SUPABASE_SECRET_KEY

// MCP / OAuth
export const MCP_AUTH_SERVER = process.env.MCP_AUTH_SERVER ?? (NEXT_PUBLIC_SUPABASE_URL ? `${NEXT_PUBLIC_SUPABASE_URL}/auth/v1` : undefined)
export const MCP_RESOURCE_URL = process.env.MCP_RESOURCE_URL ?? `${APP_URL}/mcp`
export const MCP_EXPECTED_AUDIENCE = process.env.MCP_EXPECTED_AUDIENCE ?? MCP_RESOURCE_URL

// MCP Registry HTTP (domain) authentication proof, served at
// `/.well-known/mcp-registry-auth`. Holds the full proof line
// (`v=MCPv1; k=...; p=<public-key>`) produced by the `mcp-publisher` HTTP-auth
// flow. The matching private key stays with the operator (never in the repo);
// only the public proof is served. Unset by default → the route 404s until an
// operator configures it. This is the file-based alternative to a DNS TXT
// record for verifying the `ai.aient/mcp` namespace.
export const MCP_REGISTRY_AUTH = process.env.MCP_REGISTRY_AUTH ?? ''

// Migrate legacy glimt.mcp.* scope names from env vars to aient.mcp.*
const envMigrateScope = (s: string) => s.startsWith('glimt.mcp.') ? s.replace('glimt.mcp.', 'aient.mcp.') : s

export const MCP_REQUIRED_SCOPES = (process.env.MCP_REQUIRED_SCOPES ?? 'aient.mcp.read')
  .split(',')
  .map((scope) => scope.trim())
  .filter(Boolean)
  .map(envMigrateScope)

export const MCP_INVOCATION_TOKEN_SHARED_SECRET = process.env.MCP_INVOCATION_TOKEN_SHARED_SECRET ?? ''

// All scopes that can be granted via OAuth. Superset of MCP_REQUIRED_SCOPES.
// aient.mcp.read  — read-only access (list/get problems, telemetry queries)
// aient.mcp.onboarding — limited onboarding acknowledgements through MCP
// aient.mcp.write — write access (problem remediation, future mutation tools)
export const MCP_ALLOWED_SCOPES = (process.env.MCP_ALLOWED_SCOPES ?? 'aient.mcp.read,aient.mcp.onboarding,aient.mcp.write')
  .split(',')
  .map((scope) => scope.trim())
  .filter(Boolean)
  .map(envMigrateScope)

export const MCP_DEFAULT_SCOPES = (process.env.MCP_DEFAULT_SCOPES ?? 'aient.mcp.read,aient.mcp.onboarding')
  .split(',')
  .map((scope) => scope.trim())
  .filter(Boolean)
  .map(envMigrateScope)

// OAuth broker signing key (Ed25519 private JWK JSON). Server-only.
export const MCP_OAUTH_BROKER_PRIVATE_JWK = process.env.MCP_OAUTH_BROKER_PRIVATE_JWK

export const MCP_REFRESH_TOKEN_TTL_DAYS = parseInt(process.env.MCP_REFRESH_TOKEN_TTL_DAYS ?? '60', 10) || 60
export const MCP_CODEX_ACCESS_TOKEN_TTL_DAYS = parseInt(process.env.MCP_CODEX_ACCESS_TOKEN_TTL_DAYS ?? '0', 10) || 0

// Lifetime of the /mcp bearer the agent-only signup flow (`signup` +
// `verify_email` on /mcp) hands back after email verification. It is longer
// than the standard one-hour access token because onboarding waits on browser
// steps (GitHub App install, Slack/Linear connection) that can take a while,
// and no refresh grant family exists for this token yet: when it expires the
// agent calls `signup` again (which emails a sign-in code to the existing
// account) and then `verify_email` with that code.
export const MCP_AGENT_SIGNUP_TOKEN_TTL_SECONDS =
  parseInt(process.env.MCP_AGENT_SIGNUP_TOKEN_TTL_SECONDS ?? '86400', 10) || 86_400

// Auth/NextAuth
export const NEXTAUTH_SECRET = process.env.NEXTAUTH_SECRET ?? ''

/**
 * HMAC secret shared with Pulse for signing/verifying Slack identity-link
 * (single-use ephemeral) tokens. Falls back to NEXTAUTH_SECRET so the local
 * stack works without extra config. Set explicitly in production.
 *
 * Pair with `apps/pulse/src/lib/env.ts#SLACK_LINK_TOKEN_SECRET`.
 */
export const SLACK_LINK_TOKEN_SECRET =
  process.env.SLACK_LINK_TOKEN_SECRET ?? process.env.NEXTAUTH_SECRET ?? ''

/**
 * Broker-key secret used to HMAC raw agentic-onboarding session tokens
 * before they're stored as `agentic_onboarding_sessions.session_token_hmac`.
 * The raw 32-byte token is returned to the agent ONCE in the
 * /x402/onboard response body; we never persist it. Verification
 * recomputes HMAC-SHA256(broker-key, raw-token) and compares against the
 * stored bytea (timing-safe).
 *
 * Rotation: keys live in Vault with versioned identifiers. Verification
 * accepts current AND previous key versions during a 7d overlap window
 * (Sec N-M1; full rotation cron lands v1.2). Falls back to NEXTAUTH_SECRET
 * so local stacks work without extra config; production MUST set
 * X402_SESSION_BROKER_KEY_V1 explicitly. Plan §5.3.
 */
export const X402_SESSION_BROKER_KEY_V1 =
  process.env.X402_SESSION_BROKER_KEY_V1 ?? process.env.NEXTAUTH_SECRET ?? ''

// Restate identity keys
export const RESTATE_REQUEST_IDENTITY_PUBLIC_KEY = process.env.RESTATE_REQUEST_IDENTITY_PUBLIC_KEY ?? 'publickeyv1_6qkCYL8kMcqZudCm6x9f3Yz8n2fAWQhypAbP8QBZJNDA'

export const NEXT_PUBLIC_STUB_ONBOARDING = process.env.NEXT_PUBLIC_STUB_ONBOARDING === 'true'
if (NEXT_PUBLIC_STUB_ONBOARDING && AIENT_ENV === 'prod') {
  throw new Error('STUB_ONBOARDING must not be set in production — it bypasses onboarding security checks')
}
// Restate server base URL for server-side calls
export const RESTATE_BASE_URL = process.env.RESTATE_BASE_URL ?? 'http://localhost:8080'
// Server-only bearer token for Restate ingress (Vercel -> Restate via bearer proxy)
export const RESTATE_BEARER_TOKEN = process.env.RESTATE_BEARER_TOKEN ?? ''
export const AIENT_SANDBOX_API_URL =
  process.env.AIENT_SANDBOX_API_URL
  ?? process.env.GLIMT_SANDBOX_API_URL
  ?? ''
export const AIENT_CLI_SANDBOX_API_URL =
  process.env.AIENT_CLI_SANDBOX_API_URL
  ?? (AIENT_ENV === 'prod' ? 'https://sandbox.aient.ai' : AIENT_SANDBOX_API_URL)
// Public, ticket-protected Pulse operation origin returned to the CLI. Large
// workspace uploads bypass Vercel Functions; HTTP loopback is development-only.
export const PULSE_SANDBOX_OPERATION_URL = process.env.PULSE_SANDBOX_OPERATION_URL ?? ''
// Roll back new allocations without disabling retained-session readers.
export const AIENT_CLI_MODAL_CREATE_ENABLED = process.env.AIENT_CLI_MODAL_CREATE_ENABLED === '1'
// Available by default; set 0 to disable new previews during rollback.
export const AIENT_CLI_MODAL_PREVIEW_ENABLED = (process.env.AIENT_CLI_MODAL_PREVIEW_ENABLED ?? '1') === '1'
export const AIENT_SANDBOX_API_KEY =
  process.env.AIENT_SANDBOX_API_KEY
  ?? process.env.GLIMT_SANDBOX_API_KEY
  ?? ''
export const AIENT_SANDBOX_LIFECYCLE_SIGNING_KEY =
  process.env.AIENT_SANDBOX_LIFECYCLE_SIGNING_KEY
  ?? ''
type SandboxProvider = 'local' | 'modal' | 'vercel' | 'agent-sandbox'

function parseExplicitSandboxProvider(value: string | undefined): SandboxProvider | undefined {
  if (value === 'local' || value === 'modal' || value === 'vercel' || value === 'agent-sandbox') return value
  return undefined
}

export function readExplicitSandboxProviderOverride(): SandboxProvider | undefined {
  return parseExplicitSandboxProvider(process.env.AIENT_SANDBOX_PROVIDER ?? process.env.GLIMT_SANDBOX_PROVIDER)
}

/** @deprecated Use readExplicitSandboxProviderOverride. */
export function readConfiguredSandboxProvider(): SandboxProvider | undefined {
  return readExplicitSandboxProviderOverride()
}

export const AIENT_SANDBOX_PROVIDER = readExplicitSandboxProviderOverride()
/** @deprecated Use AIENT_SANDBOX_PROVIDER. */
export const GLIMT_SANDBOX_PROVIDER = AIENT_SANDBOX_PROVIDER

// OpenTelemetry service name
export const OTEL_SERVICE_NAME = process.env.OTEL_SERVICE_NAME ?? 'aient-app'

// Public ingest destination brokered to customer sandbox processes. It is not
// the application's own (possibly private/local) collector endpoint.
export const AIENT_OTEL_EXPORTER_OTLP_ENDPOINT =
  process.env.AIENT_OTEL_EXPORTER_OTLP_ENDPOINT ?? 'https://ingest.aient.ai'

// OpenTelemetry browser configuration
// NOTE: `NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT` is the base OTLP endpoint (no signal path).
// The browser SDK derives `.../v1/traces` and `.../v1/logs` from it.
export const NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT =
  process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT
  ?? (AIENT_ENV === 'prod' ? 'https://ingest.aient.ai' : `${APP_URL}/api/otlp`)
export const NEXT_PUBLIC_OTEL_SERVICE_NAME = process.env.NEXT_PUBLIC_OTEL_SERVICE_NAME ?? 'aient-browser'

// Google Tag Manager (client-side analytics/marketing).
// The GA4 configuration tag (stream G-36KVNWXKCM) lives INSIDE this container —
// loading it is what activates data collection; do not also emit gtag('config').
// Defaults to the real container only in prod so dev/preview/test never pollute
// analytics; set NEXT_PUBLIC_GTM_CONTAINER_ID locally to opt in.
const DEFAULT_GTM_CONTAINER_ID = 'GTM-TXNNR3K4'
export const NEXT_PUBLIC_GTM_CONTAINER_ID =
  process.env.NEXT_PUBLIC_GTM_CONTAINER_ID
  ?? (AIENT_ENV === 'prod' ? DEFAULT_GTM_CONTAINER_ID : '')
// Optional first-party serving path/URL for server-side GTM (sGTM). Empty → load
// from www.googletagmanager.com. For MULTI-DOMAIN first-party serving (aient.ai,
// aient.se, aient.dk, …) set a RELATIVE path '/collect/gtm.js' so each domain
// loads the container same-origin and sets its own first-party cookies; an
// absolute 'https://aient.ai/collect/gtm.js' pins every domain to one host and
// is only correct for a single-domain deployment.
export const NEXT_PUBLIC_GTM_SCRIPT_URL = process.env.NEXT_PUBLIC_GTM_SCRIPT_URL ?? ''
// GTM renders only when a container id is configured and we're not in test mode.
export const GTM_ENABLED = Boolean(NEXT_PUBLIC_GTM_CONTAINER_ID) && !NEXT_PUBLIC_TEST_MODE

// GA4 measurement id (the GA4 stream configured INSIDE the GTM container). Used
// client-side to read the GA4 client id via `gtag('get', …, 'client_id')` so the
// server-side early-access conversion can attribute the lead to a browser session.
// Same prod-only default policy as the container id — dev/preview/test stay empty
// so gtag is never queried (and no client id is sent) outside production.
const DEFAULT_GA4_MEASUREMENT_ID = 'G-36KVNWXKCM'
export const NEXT_PUBLIC_GA4_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID
  ?? (AIENT_ENV === 'prod' ? DEFAULT_GA4_MEASUREMENT_ID : '')

// Upstream for the first-party /collect/* rewrite to the in-cluster sGTM tagging
// server (e.g. 'https://sgtm.aient.ai'). Empty → the rewrite is not registered.
export const GTM_SGTM_UPSTREAM = process.env.GTM_SGTM_UPSTREAM ?? ''

// OpenTelemetry Node configuration
// NOTE: `OTEL_EXPORTER_OTLP_ENDPOINT` is the base OTLP endpoint (no signal path).
// If set, the SDK derives `/v1/traces` (and `/v1/logs`) from it.
export const OTEL_EXPORTER_OTLP_ENDPOINT =
  process.env.OTEL_EXPORTER_OTLP_ENDPOINT
  ?? (AIENT_ENV === 'prod' ? 'https://ingest.aient.ai' : 'http://localhost:4318')
export const OTEL_EXPORTER_OTLP_GRPC_ENDPOINT =
  process.env.OTEL_EXPORTER_OTLP_GRPC_ENDPOINT
  ?? (AIENT_ENV === 'prod' ? 'https://ingest.aient.ai:4317' : 'http://localhost:4317')

// Base OTLP/HTTP endpoint the Vercel-drain receiver forwards normalized telemetry
// to (no signal path; the receiver appends /v1/logs or /v1/traces). Defaults to the
// same gateway the SDKs use so drain ingest rides the existing authcontext/filterdb
// pipeline. Server-side only.
export const VERCEL_DRAIN_FORWARD_ENDPOINT =
  process.env.VERCEL_DRAIN_FORWARD_ENDPOINT ?? OTEL_EXPORTER_OTLP_ENDPOINT

// Public base URL where the otel-collector's native Vercel-drain receiver (Route
// B) is reachable; the customer pastes `${base}/vercel/<token>/<signal>` into
// Vercel's drain settings. Vercel's servers POST here, so it MUST be a public,
// internet-reachable origin in EVERY environment — a localhost or app-proxy URL
// would 404. Defaults to the prod ingest gateway; override in self-host/preview.
// NEXT_PUBLIC_ so the value is identical server- and client-side (it's rendered
// in the settings UI). Never log the full URL: the token segment is a credential.
export const NEXT_PUBLIC_VERCEL_DRAIN_INGEST_BASE =
  process.env.NEXT_PUBLIC_VERCEL_DRAIN_INGEST_BASE ?? 'https://ingest.aient.ai'
export const NEXT_PUBLIC_AWS_FIREHOSE_INGEST_BASE =
  process.env.NEXT_PUBLIC_AWS_FIREHOSE_INGEST_BASE ?? NEXT_PUBLIC_VERCEL_DRAIN_INGEST_BASE

// ClickHouse client configuration
export const CLICKHOUSE_URL = process.env.CLICKHOUSE_URL ?? 'http://localhost:8123'
export const CLICKHOUSE_DATABASE = process.env.CLICKHOUSE_DATABASE ?? 'observability'
export const CLICKHOUSE_USERNAME = process.env.CLICKHOUSE_USERNAME ?? 'otel'
export const CLICKHOUSE_PASSWORD = process.env.CLICKHOUSE_PASSWORD ?? 'otel_password'
// Server-only bearer token for ClickHouse ingress (Vercel -> bearer-proxy -> ClickHouse).
// If set, ClickHouse HTTP calls should use Bearer auth against the proxy instead of basic auth.
export const CLICKHOUSE_BEARER_TOKEN = process.env.CLICKHOUSE_BEARER_TOKEN ?? ''
// Verified start of complete receipt-time telemetry coverage. Set only after
// every ingress producer and ClickHouse view is observed writing receipts.
export const TELEMETRY_RECEIPT_COVERAGE_START = process.env.TELEMETRY_RECEIPT_COVERAGE_START

// Admin secret for admin endpoints
export const ADMIN_SECRET = process.env.ADMIN_SECRET

// Aient SDK configuration - for Aient to monitor itself
// Same Organisation ID for both Node and browser telemetry
export const NEXT_PUBLIC_AIENT_ORG_ID =
  process.env.NEXT_PUBLIC_AIENT_ORG_ID
  ?? 'e2fe8865-4d2f-4a2b-a0a9-b4f5a735c03b'
export const HAS_EXPLICIT_AIENT_ORG_ID = Boolean(process.env.NEXT_PUBLIC_AIENT_ORG_ID)

export const AIENT_DEV_SELF_MONITORING_PUBLISHABLE_KEY =
  'pk_live_11111111111111111111111111111111'

function defaultBrowserAientPublishableKey(): string {
  return AIENT_ENV === 'dev' || AIENT_ENV === 'test'
    ? AIENT_DEV_SELF_MONITORING_PUBLISHABLE_KEY
    : ''
}

// Aient's production self-monitoring key belongs to an environment configured as prod (Vercel
// production sets VERCEL_ENV / NEXT_PUBLIC_VERCEL_ENV), never to the NODE_ENV fallback: a local
// `next build && next start` is production-mode but must not report into Aient's production.
function selfMonitoringPublishableKey(): string {
  const configured = process.env.NEXT_PUBLIC_AIENT_PUBLISHABLE_KEY ?? defaultBrowserAientPublishableKey()
  if (AIENT_ENV !== 'prod' || normalizeAientEnv(CONFIGURED_AIENT_ENV) === 'prod' || !configured) return configured
  console.warn('Aient self-monitoring export is disabled: this production-mode build has no configured AIENT_ENV '
    + '(AIENT_ENV, VERCEL_ENV, ...). Set AIENT_ENV=prod to report into Aient production deliberately.')
  return ''
}

// Publishable key (safe to expose in browser, validated against publishable_keys table)
export const NEXT_PUBLIC_AIENT_PUBLISHABLE_KEY = selfMonitoringPublishableKey()

// Explicit hosted or harness configuration wins, including empty (disabled).
// The local seed remains the fallback only when no key was configured.
export const AIENT_SELF_MONITORING_PUBLISHABLE_KEY = NEXT_PUBLIC_AIENT_PUBLISHABLE_KEY

// Telemetry routing identity is independent of the app's auth/billing/test mode.
export const AIENT_TELEMETRY_ENVIRONMENT =
  process.env.NEXT_PUBLIC_AIENT_TELEMETRY_ENVIRONMENT ?? AIENT_ENV

function redactPublishableKey(value: string | undefined): string {
  if (!value) return '[missing]'
  if (value.length <= 12) return '[redacted]'
  return `${value.slice(0, 7)}…${value.slice(-4)}`
}

// Vercel Sandbox credentials (optional locally; required in production)
export const SANDBOX_VERCEL_TEAM_ID = process.env.SANDBOX_VERCEL_TEAM_ID ?? 'aient'
export const SANDBOX_VERCEL_PROJECT_ID = process.env.SANDBOX_VERCEL_PROJECT_ID ?? 'aient'
export const SANDBOX_VERCEL_TOKEN = process.env.SANDBOX_VERCEL_TOKEN!

// Sandbox timeout
export const SANDBOX_TIMEOUT = process.env.SANDBOX_TIMEOUT != null ? ms(process.env.SANDBOX_TIMEOUT as unknown as ms.StringValue) : 5 * 60 * 1000 // 5m default

// Install dev tools (jq, rg, fd, etc.) on sandbox creation - adds ~10-15s latency
export const SANDBOX_INSTALL_DEV_TOOLS = process.env.SANDBOX_INSTALL_DEV_TOOLS === 'true'

// FizzBee CLI binary path (auto-detected from PATH if not set)
export const FIZZBEE_BINARY_PATH = process.env.FIZZBEE_BINARY_PATH ?? ''

// FizzBee API key for bearer auth (optional — only enforced when set)
export const FIZZBEE_API_KEY = process.env.FIZZBEE_API_KEY

// FizzBee CORS allowed origins (comma-separated)
export const FIZZBEE_ALLOWED_ORIGINS = (process.env.FIZZBEE_ALLOWED_ORIGINS ?? 'http://localhost:3100')
  .split(',')
  .map(o => o.trim())

// App access gate - set to a password to require it before accessing the UI
// Empty string (default) = gate disabled
export const GATE_PASSWORD = process.env.GATE_PASSWORD ?? ''

export const LINEAR_CLIENT_ID = process.env.LINEAR_CLIENT_ID ?? 'd9e8e463429f424a9e7a3b5514efce4c'
export const LINEAR_CLIENT_SECRET = process.env.LINEAR_CLIENT_SECRET
export const LINEAR_SCOPES = process.env.LINEAR_SCOPES ?? 'read,write,issues:create,app:assignable,app:mentionable'
export const LINEAR_WEBHOOK_SECRET = process.env.LINEAR_WEBHOOK_SECRET

// Loops.so webhook signing secret (Svix/standard webhooks format: whsec_<base64>).
// The default is a fabricated dev/test value so local webhooks verify; production
// fails fast below unless LOOPS_WEBHOOK_SECRET is configured explicitly.
const DEFAULT_LOOPS_WEBHOOK_SECRET = 'whsec_ZGV2ZGV2ZGV2ZGV2ZGV2ZGV2ZGV2ZGV2ZGV2ZGV2ZGV2ZGV2'
const EXPLICIT_LOOPS_WEBHOOK_SECRET = process.env.LOOPS_WEBHOOK_SECRET?.trim()
export const LOOPS_WEBHOOK_SECRET = EXPLICIT_LOOPS_WEBHOOK_SECRET || DEFAULT_LOOPS_WEBHOOK_SECRET

// x402 + Bazaar paywall configuration.
// Source of truth: docs/plans/2026-05-26-feat-x402-bazaar-listing-plan.md (§6, §7).
// Dev defaults target the public x402.org facilitator on Base Sepolia (no signup);
// production swaps to the CDP facilitator on Base mainnet so settled payments
// are indexed by Bazaar.
const DEFAULT_X402_PAY_TO = '0x7fe8569649758CE2b965fbC6acDa2a1e23a1D2C0'
const DEFAULT_X402_NETWORK = 'eip155:84532'
const DEFAULT_X402_FACILITATOR_URL = 'https://x402.org/facilitator'

// CAIP-2 chain identifier. eip155:84532 = Base Sepolia (testnet), eip155:8453 = Base mainnet.
export const X402_NETWORK = process.env.X402_NETWORK ?? DEFAULT_X402_NETWORK
// EVM payee address receiving settled USDC. We never custody the private key.
export const X402_PAY_TO = process.env.X402_PAY_TO ?? DEFAULT_X402_PAY_TO
// Facilitator base URL. CDP is required for Bazaar indexing; x402.org for dev.
export const X402_FACILITATOR_URL = process.env.X402_FACILITATOR_URL ?? DEFAULT_X402_FACILITATOR_URL

// Coinbase Developer Platform credentials. When both id+secret are present the
// facilitator factory returns a CDP HTTPFacilitatorClient; otherwise it falls
// back to x402.org. See apps/aient-app/src/lib/x402/facilitator.ts.
export const CDP_API_KEY_ID = process.env.CDP_API_KEY_ID ?? ''
export const CDP_API_KEY_SECRET = process.env.CDP_API_KEY_SECRET ?? ''
export const CDP_API_KEY_NAME = process.env.CDP_API_KEY_NAME ?? ''

// CDP wallet secret — separate from the API key pair. Required ONLY when
// scripts (Phase 6.5 Sepolia smoke, Phase 7 mainnet warmup) need to mint
// or use a CDP-custodied EVM account as a *buyer*. The server runtime
// never touches this; only the warmup/smoke scripts under scripts/
// read it. Loaded from secrets/x402-cdp.txt by those scripts.
export const CDP_WALLET_SECRET = process.env.CDP_WALLET_SECRET ?? ''

// Optional 0x-prefixed hex private key for scripted x402 buyer flows
// (Bazaar dry-run, E2E specs). Testnet only — never set in production.
export const X402_DRYRUN_PRIVATE_KEY = process.env.X402_DRYRUN_PRIVATE_KEY ?? ''

// E2E-only facilitator override. When set, the facilitator factory
// returns an anonymous HTTPFacilitatorClient pointed at this URL,
// regardless of CDP credentials. Used by apps/aient-app/e2e against
// the mock-facilitator docker service so Playwright tests do not
// hit a real chain. Production MUST leave this unset; the factory
// throws if it is set in a non-test environment.
export const X402_E2E_FACILITATOR_URL = process.env.X402_E2E_FACILITATOR_URL ?? ''

// E2E agent model override — runtime functions to avoid ESM hoisting trap.
// When set (e.g. in .env.e2e), chat routes use this model instead of the hardcoded default.
// Returns undefined when unset so callers can fall back to their own default.
import type { SupportedModel, ThinkingLevel } from '@aient/types/basic-agent-v2'

export function getE2EAgentModel(): SupportedModel | undefined {
  const v = process.env.E2E_AGENT_MODEL
  return v ? v as SupportedModel : undefined
}

export function getE2EAgentThinking(): ThinkingLevel | undefined {
  const v = process.env.E2E_AGENT_THINKING
  return v ? v as ThinkingLevel : undefined
}

// Git Metadata
export const GIT_COMMIT_SHA = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA
  ?? process.env.NEXT_PUBLIC_COMMIT_SHA
  ?? process.env.COMMIT_SHA
export const GIT_COMMIT_REF = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF
  ?? process.env.NEXT_PUBLIC_COMMIT_REF
  ?? process.env.COMMIT_REF

// In prod, fail-fast if Stripe env vars are still using sandbox fallbacks.
// This keeps local/dev "just work" behavior while preventing accidental prod misconfiguration.
// IMPORTANT: Next.js evaluates server modules during `next build` (e.g. when collecting page data).
// We must NOT require runtime-only production secrets at build time (CI/Docker builds),
// otherwise builds fail even though the runtime environment will provide the secrets.
const IS_NEXT_PROD_BUILD = process.env.NEXT_PHASE === 'phase-production-build' || process.env.NEXT_PHASE === 'phase-export'

if (typeof window === 'undefined' && AIENT_ENV === 'prod' && !IS_NEXT_PROD_BUILD) {
  const bad: string[] = []

  if (STRIPE_SECRET_KEY === DEFAULT_STRIPE_SECRET_KEY) bad.push('STRIPE_SECRET_KEY')
  if (STRIPE_SNAPSHOT_WEBHOOK_SECRET === DEFAULT_STRIPE_SNAPSHOT_WEBHOOK_SECRET) bad.push('STRIPE_SNAPSHOT_WEBHOOK_SECRET')
  if (STRIPE_THIN_WEBHOOK_SECRET === DEFAULT_STRIPE_THIN_WEBHOOK_SECRET) bad.push('STRIPE_THIN_WEBHOOK_SECRET')
  if (NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID === DEFAULT_NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID) bad.push(`NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID=${NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID}`)
  if (NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID === DEFAULT_NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID) bad.push(`NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID=${NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID}`)
  if (NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY === DEFAULT_NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) bad.push(`NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY=${NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY}`)

  if (!NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID) bad.push('NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID[null]')
  if (!NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID) bad.push('NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID[null]')

  if (bad.length > 0) {
    throw new Error(
      `Stripe production configuration is missing or using sandbox defaults: ${bad.join(', ')}`
      + ` (AIENT_ENV=${AIENT_ENV}, NODE_ENV=${NODE_ENV})`,
    )
  }

  // Webhook HMAC secrets — must be configured in production to prevent signature bypass.
  // Empty or default secrets allow attackers to forge valid webhook signatures.
  const missingWebhookSecrets: string[] = []
  if (!GITHUB_APP_WEBHOOK_SECRET) missingWebhookSecrets.push('GITHUB_APP_WEBHOOK_SECRET')
  if (!SLACK_SIGNING_SECRET) missingWebhookSecrets.push('SLACK_SIGNING_SECRET')
  if (!LINEAR_WEBHOOK_SECRET) missingWebhookSecrets.push('LINEAR_WEBHOOK_SECRET')
  if (!EXPLICIT_LOOPS_WEBHOOK_SECRET || EXPLICIT_LOOPS_WEBHOOK_SECRET === DEFAULT_LOOPS_WEBHOOK_SECRET) {
    missingWebhookSecrets.push('LOOPS_WEBHOOK_SECRET')
  }

  if (missingWebhookSecrets.length > 0) {
    throw new Error(
      `Webhook HMAC secrets missing or using dev defaults in production: ${missingWebhookSecrets.join(', ')}`
      + ` (AIENT_ENV=${AIENT_ENV}, NODE_ENV=${NODE_ENV})`,
    )
  }

  // Production must never silently inherit the development fallback. The explicitly configured
  // value remains authoritative because changing an encryption key requires migrating ciphertext.
  if (!EXPLICIT_VAULT_KEY) {
    throw new Error(
      `VAULT_KEY must be configured explicitly in production`
      + ` (AIENT_ENV=${AIENT_ENV}, NODE_ENV=${NODE_ENV})`,
    )
  }
  if (MCP_CONNECTION_COMMAND_DIGEST_KEYS.length === 0) {
    throw new Error(
      'MCP_CONNECTION_COMMAND_DIGEST_KEYS must provide a shared current/previous key ring for customer MCP in production',
    )
  }
}

// print the key URL:s
if (DEBUG) {
  console.log('==== ENV ====')
  console.log('APP_URL', APP_URL)
  console.log('GITHUB_APP_ID', GITHUB_APP_ID)
  console.log('GITHUB_APP_CLIENT_ID', GITHUB_APP_CLIENT_ID)
  console.log('SANDBOX_VERCEL_TEAM_ID', SANDBOX_VERCEL_TEAM_ID)
  console.log('SANDBOX_VERCEL_PROJECT_ID', SANDBOX_VERCEL_PROJECT_ID)

  console.log('==== SUPABASE ====')
  console.log('NEXT_PUBLIC_SUPABASE_URL', NEXT_PUBLIC_SUPABASE_URL)
  console.log('NEXT_PUBLIC_SUPABASE_REDIRECT_URL', NEXT_PUBLIC_SUPABASE_REDIRECT_URL)

  console.log('==== RESTATE ====')
  console.log('RESTATE_BASE_URL', RESTATE_BASE_URL)

  console.log('==== OTEL ====')
  console.log('NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT', NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT)
  console.log('NEXT_PUBLIC_OTEL_SERVICE_NAME', NEXT_PUBLIC_OTEL_SERVICE_NAME)
  console.log('NEXT_PUBLIC_AIENT_ORG_ID', NEXT_PUBLIC_AIENT_ORG_ID)
  console.log('NEXT_PUBLIC_AIENT_PUBLISHABLE_KEY', redactPublishableKey(NEXT_PUBLIC_AIENT_PUBLISHABLE_KEY))
}
